import React from 'react';
import styled from 'styled-components';
import { cssColorValue, screenXxsMax, screenXsMin, screenSmMin, screenSmMax, screenMdMin, Text, cssSpacing, } from '@spotify-internal/encore-web';
import { articleMarkdown, defaultMarkdown } from '../../ui/Markdown/Markdown';
import { ShareButtonsComponent } from '../../ui/ShareButtons/';
import { ImageAspectBox } from '../../ui';
const maxWidthOfImage = '800px';
const maxWidthOfText = '664px';
const Article = styled.div `
  background-color: ${cssColorValue('backgroundBase')};
  margin-bottom: ${cssSpacing('looser-5')};
  @media (max-width: ${screenXxsMax}) {
    margin-left: ${cssSpacing('base')};
    margin-right: ${cssSpacing('base')};
  }

  @media (min-width: ${screenXsMin}) and (max-width: ${screenSmMax}) {
    margin-left: 52px;
    margin-right: 52px;
  }

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}) {
    width: ${maxWidthOfText};
    margin: 0 auto ${cssSpacing('looser-5')};
  }

  @media (min-width: ${screenMdMin}) {
    width: ${maxWidthOfImage};
    margin: 0 auto ${cssSpacing('looser-5')};
  }
`;
const IntroductionWrapper = styled.div `
  padding-bottom: 0;

  @media (min-width: ${screenMdMin}) {
    max-width: 664px;
    margin: auto;
  }

  @media (max-width: ${screenXxsMax}) {
    margin-bottom: ${cssSpacing('looser')};
  }

  @media (min-width: ${screenXsMin}) {
    margin-bottom: ${cssSpacing('tighter-4')};
  }
`;
const Introduction = styled(Text).attrs({
    variant: 'titleMedium',
}) `
  @media (max-width: ${screenXxsMax}) {
    font-size: 1.75rem;
    line-height: 2.25rem;
  }

  @media (min-width: ${screenXsMin}) {
    font-size: 2rem;
    line-height: 2.5rem;
  }

  a {
    color: ${cssColorValue('textBase')};
    text-decoration: underline;
    &:hover {
      color: ${cssColorValue('textBrightAccent')};
    }
  }
`;
const BodyWrapper = styled.div ``;
const Body = styled.div `
  @media (min-width: ${screenMdMin}) {
    & > * {
      width: ${maxWidthOfText};
      margin: auto;
    }

    p.article-picture,
    div {
      width: ${maxWidthOfImage};
    }
  }

  iframe {
    display: block;
    width: 100%;
    margin: auto;
    margin-bottom: ${cssSpacing('tighter-4')};
  }

  ${ImageAspectBox} {
    width: 100%;
    aspect-ratio: 16 / 9;
    margin: auto;

    iframe {
      height: 100%;
      padding-bottom: 0;
    }
  }

  img,
  div > iframe {
    width: 100%;
    margin-bottom: ${cssSpacing('looser-4')};
  }

  figcaption {
    text-align: left;
  }

  a {
    text-decoration: underline;
    color: ${cssColorValue('textBase')};
    &:hover,
    &:focus:not(:disabled),
    &:active:not(:disabled),
    &.highlight {
      color: ${cssColorValue('textBrightAccent')};
      -webkit-tap-highlight-color: ${cssColorValue('textBrightAccent')};
    }
  }

  .spotify-embed-link {
    display: inline-block;
    background-color: #1db954;
    color: white !important;
    text-decoration: none !important;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 600;
    margin: 8px 0;

    &:hover {
      background-color: #1ed760;
      color: white !important;
    }
  }

  ul,
  ol {
    padding-left: ${cssSpacing('looser')};
  }

  ol > li,
  ul > li {
    margin-bottom: ${cssSpacing('base')};
  }

  p {
    margin-top: 0;
  }

  p,
  ul,
  ol {
    padding-block-end: 0;
    margin-bottom: ${cssSpacing('looser')};
  }

  h1,
  h2,
  h3,
  h4 {
    margin-top: 0;
  }

  h3 {
    margin-bottom: ${cssSpacing('base')};
  }

  blockquote {
    border: 0;
    padding: 0;
    margin: ${cssSpacing('looser')} auto;

    p {
      color: ${cssColorValue('textBrightAccent')};
      font-weight: 450;
      letter-spacing: -0.02em;
    }
  }

  @media (max-width: ${screenXxsMax}) {
    :not(iframe) {
      margin-bottom: ${cssSpacing('looser-4')};
    }

    p + :not(p, blockquote, ul, ol, iframe),
    p + .article-picture {
      margin-top: ${cssSpacing('looser-4')};
    }

    h2 {
      font-size: 1.75rem;
      line-height: 2.25rem;
    }

    h3 {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }

    p,
    ol > li,
    ul > li {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }

    blockquote p {
      font-size: 1.625rem;
      line-height: 2.25rem;
    }
  }

  @media (min-width: ${screenXsMin}) {
    :not(iframe) {
      margin-bottom: ${cssSpacing('looser-3')};
    }

    p + :not(p, blockquote, ul, ol, iframe),
    p + .article-picture {
      margin-top: ${cssSpacing('looser-4')};
    }

    h2 {
      font-size: 2rem;
    }

    h3 {
      font-size: 1.25rem;
    }

    p,
    ol > li,
    ul > li {
      font-size: 1.25rem;
      line-height: ${cssSpacing('looser-2')};
    }

    blockquote p {
      font-size: 1.75rem;
      line-height: ${cssSpacing('looser-4')};
    }
  }
`;
const BottomSection = styled.div `
  @media (min-width: ${screenMdMin}) {
    width: ${maxWidthOfText};
    margin: auto;
  }

  display: flex;
  flex-direction: column;
  border-top: 1px solid ${cssColorValue('decorativeSubdued')};
  padding-top: ${cssSpacing('tighter')};

  p {
    margin-bottom: ${cssSpacing('looser-4')};
  }
`;
const ShareButtonContainer = styled.div `
  display: flex;
  flex-direction: column;

  & > div {
    margin-top: -2px;
    padding-bottom: 0;
  }
`;
const processSpotifyEmbeds = (html) => {
    const spotifyRegex = /\[play-button spotify:([^\]]+)\]/g;
    if (!spotifyRegex.test(html)) {
        return html;
    }
    return html.replace(spotifyRegex, (_match, spotifyId) => {
        const spotifyUrl = `https://open.spotify.com/${spotifyId.replace(':', '/')}`;
        return `<a href="${spotifyUrl}" target="_blank" rel="noopener noreferrer" class="spotify-embed-link">Listen on Spotify</a>`;
    });
};
export const ArticleModule = ({ introduction = '', body = '', initialDropcap = false, copy, shareUrl, }) => {
    const markDown = articleMarkdown(body, initialDropcap);
    const processedMarkdown = processSpotifyEmbeds(markDown);
    return (React.createElement(Article, null,
        React.createElement(IntroductionWrapper, null,
            React.createElement(Introduction, { dangerouslySetInnerHTML: {
                    __html: defaultMarkdown(introduction),
                } })),
        React.createElement(BodyWrapper, null,
            React.createElement(Body, { dangerouslySetInnerHTML: {
                    __html: processedMarkdown,
                } })),
        React.createElement(BottomSection, null,
            React.createElement(Text, { as: "p", variant: "bodyMedium" }, copy.shareTitle),
            React.createElement(ShareButtonContainer, null,
                React.createElement(Text, { variant: "bodyMediumBold", paddingBottom: "base" }, copy.share),
                React.createElement(ShareButtonsComponent, { facebookShareLabel: copy.facebookShareLabel, twitterShareLabel: copy.twitterShareLabel, copyLinkLabel: copy.copyLinkLabel, shareUrl: shareUrl })))));
};
